import React from 'react';
import s from './s.module.less';
import AIChat from 'components/AIChat';
import SetGolive from './components/SetGolive';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import ProfileApproved from './components/ProfileApproved';
import ProfileReject from './components/ProfileReject';
import ProfileReviewing from './components/ProfileReviewing';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { Spin } from 'antd';
import { getUrlParam } from 'utils/common';
import { EProfileStatus } from 'types/channel';
import { saveStep } from 'api/ppaOnboarding';
import { EPPAStep } from 'types/ppaOnboarding';
import { trackHeapEvent } from 'utils/analytics';

enum EStep {
    GO_LIVE = 'go_live',
    PROFILE_APPROVED = 'profile_approved',
    PROFILE_REJECTED = 'profile_rejected',
    PROFILE_REVIEWING = 'profile_reviewing',
}

export default function PPAOnboardingFinishPage() {
    const [step, setStep] = React.useState<EStep | undefined>();
    const navigate = useNavigate();
    const [homeInfo] = useProviderHomeInfo();
    const handleGoToAdvancedSetup = async () => {
        try {
            trackHeapEvent('WebApp - PPA Onboarding Finish - Click - Go to Advanced Setup', {
                currentStep: step,
            });
            // 保存当前步骤 STEP_19
            const saveStepRes = await saveStep(EPPAStep.STEP_19);
            if (saveStepRes.error) {
                trackHeapEvent('WebApp - PPA Onboarding Finish - View - Save Step Failed', {
                    currentStep: step,
                    error: saveStepRes.error,
                });
                // eslint-disable-next-line no-console
                console.error('Failed to save step:', saveStepRes.error);
                return;
            }

            trackHeapEvent('WebApp - PPA Onboarding Finish - View - Go to Advanced Setup Success', {
                currentStep: step,
            });
            navigate(`${PATH.PPA_ONBOARDING_ADVANCE_SETTING}`);
        } catch (error) {
            trackHeapEvent('WebApp - PPA Onboarding Finish - View - Go to Advanced Setup Error', {
                currentStep: step,
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            // eslint-disable-next-line no-console
            console.error('Error in handleGoToAdvancedSetup:', error);
        }
    };

    const handleGoToEditProfile = () => {
        trackHeapEvent('WebApp - PPA Onboarding Finish - Click - Go to Edit Profile', {
            currentStep: step,
        });
        navigate(`${PATH.PPA_ONBOARDING_REVIEW}`);
    };

    React.useEffect(() => {
        const from = getUrlParam('from');
        if (from && (from.toLowerCase() === 'onboarding' || from.toLowerCase() === 'email')) {
            setStep(EStep.GO_LIVE);
        } else if (homeInfo) {
            if (homeInfo.uniprofileStatus === EProfileStatus.APPROVED) {
                setStep(EStep.PROFILE_APPROVED);
                return;
            }
            if (homeInfo.uniprofileStatus === EProfileStatus.REJECTED) {
                setStep(EStep.PROFILE_REJECTED);
                return;
            }
            setStep(EStep.PROFILE_REVIEWING);
        }
    }, [homeInfo]);

    React.useEffect(() => {
        if (step) {
            trackHeapEvent('WebApp - PPA Onboarding Finish - View - Page', {
                step,
                hasHomeInfo: !!homeInfo,
                uniprofileStatus: homeInfo?.uniprofileStatus,
            });
        }
    }, [step, homeInfo]);

    const getAIChatPageId = React.useMemo((): EAIChatPageId => {
        if (!step) {
            return EAIChatPageId.PROVIDER_ONBOARDING_FINISH_GO_LIVE;
        }
        switch (step) {
            case EStep.GO_LIVE:
                return EAIChatPageId.PROVIDER_ONBOARDING_FINISH_GO_LIVE;
            case EStep.PROFILE_APPROVED:
                return EAIChatPageId.PROVIDER_ONBOARDING_FINISH_APPROVED;
            case EStep.PROFILE_REJECTED:
                return EAIChatPageId.PROVIDER_ONBOARDING_FINISH_REJECTED;
            case EStep.PROFILE_REVIEWING:
                return EAIChatPageId.PROVIDER_ONBOARDING_FINISH_REVIEWING;
            default:
                return EAIChatPageId.PROVIDER_ONBOARDING_FINISH_GO_LIVE;
        }
    }, [step]);

    return (
        <div className={s.wrap}>
            <PPAOnboardingHeader showLogout />
            <div className={s.content}>
                <div className={s.contentWrap}>
                    <div className={s.contentIn}>
                        {
                            step === undefined && <Spin />
                        }
                        {
                            step === EStep.GO_LIVE && <SetGolive onGoToAdvancedSetup={handleGoToAdvancedSetup} />
                        }
                        {
                            step === EStep.PROFILE_APPROVED && <ProfileApproved onGoToAdvancedSetup={handleGoToAdvancedSetup} />
                        }
                        {
                            step === EStep.PROFILE_REJECTED && <ProfileReject onGoToEditProfile={handleGoToEditProfile} />
                        }
                        {
                            step === EStep.PROFILE_REVIEWING && <ProfileReviewing onGoToAdvancedSetup={handleGoToAdvancedSetup} />
                        }
                    </div>
                    <div className={s.chatWrap}>
                        <AIChat
                            pageId={getAIChatPageId}
                            defaultShow={false}
                            buttonStyle={{
                                bottom: '45px',
                            }}
                        />
                    </div>
                </div>

            </div>

        </div>
    );
}
